import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { useAudio } from "../../../../../lib/use-audio";
import { DateTime, Schema } from "effect";
import { Button } from "@ascleo/ui/components/ui/button";
import { ArrowRight, Check, Pencil, Plus, Save, SkipForward, Stethoscope, X } from "lucide-react";
import {
  HausarztInput,
  eligibleContactKind,
} from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
import type { ReviewContact } from "@ascleo/frontdesk-contracts/patient-info/review";
import {
  patientInfoRpcClient,
  revokePatientInfoQuietly,
} from "../../../../../lib/rpc/patient-info.client";
import {
  useCheckInContext,
  useCheckInSelector,
  useCheckInSend,
} from "../context";
import { checkInSelectors } from "../selectors";
import { KioskStep } from "../../../../../components/kiosk-step";
import { KioskTextField } from "../../../../../components/kiosk-field";
import { KioskStatePanel } from "../../../../../components/kiosk-state-panel";
import {
  CategoryCard,
  ContactCategoryBody,
  HausarztCategoryBody,
  RevealButton,
} from "./patient-info/CategoryCard";
import { ContactCategoryEditor } from "./patient-info/ContactCategoryEditor";
import { HandlingChip } from "./patient-info/HandlingChip";
import { ReviewBalanceLine, reviewPrimaryLabelKey } from "./patient-info/ReviewActions";
import { buildCategoryViews } from "./patient-info/category-model";
import {
  balanceParts,
  type PendingAddition,
  type PendingProposal,
  proposalFieldName,
  summarizeStagedChanges,
} from "./patient-info/staged-changes";
import { useReviewForm } from "./patient-info/use-review-form";
import { OutcomeRow } from "./patient-info/OutcomeRow";
import { ExpiredScreen, NearExpiryNotice } from "./patient-info/ExpiryStates";
import { NEAR_EXPIRY_MS, initialRemainingMs } from "./patient-info/expiry";
import {
  buildSubmittedProposal,
  hasUnfinishedOutcome,
  mergeOutcomes,
  type SubmittedProposal,
} from "./patient-info/submitted-proposal";
import type { FieldOutcome } from "@ascleo/frontdesk-contracts/patient-info/submit";

/** A revealed contact value is masked again after this many milliseconds (KC-56). */
const REVEAL_AUTO_HIDE_MS = 15_000;

export {
  buildHausarztProposal,
  buildOperations,
  type HausarztProposal,
  type PendingAddition,
} from "./patient-info/staged-changes";

/**
 * Patient-info review step: a three-line check sheet for phone, email, and task-only Hausarzt.
 *
 * The common case is a patient glancing at one masked value per enabled category and tapping "Alles
 * korrekt" once, so the resting surface is a scan rather than a form: additional contacts stay
 * collapsed, values stay masked, and each row states up front whether its changes are saved directly
 * or handed to reception. Editing opens inline on the same screen and stays reversible until one
 * final commit. Any save failure never disturbs the completed check-in.
 */
export function PatientInfoReviewStep() {
  const { t } = useTranslation();
  const currentState = useCheckInSelector(checkInSelectors.currentState);
  useAudio(
    currentState === "patientInfoResult"
      ? "checkIn-patientInfoResult"
      : "checkIn-patientInfoReview",
  );
  const send = useCheckInSend();
  const context = useCheckInContext();

  const handle = context.egkAttestation;
  const submissionId = useMemo(
    () => `sub-${globalThis.crypto.randomUUID()}`,
    [],
  );
  const contacts = useMemo(
    () => context.patientInfoReview?.contacts ?? [],
    [context.patientInfoReview],
  );
  // Memoised because the fallback is a fresh literal: without this, every render produces a new
  // policy object and the category memo below rebuilds the whole check sheet each time.
  const enabledFields = useMemo(
    () =>
      context.patientInfoReview?.fields ?? {
        phone: false,
        email: false,
        hausarzt: false,
      },
    [context.patientInfoReview],
  );
  const ordinary = useMemo(
    () => contacts.filter((contact) => contact.protection === undefined),
    [contacts],
  );
  const currentHausarzt = enabledFields.hausarzt
    ? (context.patientInfoReview?.hausarzt ?? null)
    : null;
  const guarded = useMemo(
    () => contacts.filter((contact) => contact.protection !== undefined),
    [contacts],
  );
  const defaultValues = useMemo<Record<string, string>>(
    () => ({
      ...Object.fromEntries(
        ordinary.map((contact) => [contact.itemId, contact.value]),
      ),
      // A proposal starts empty; it only becomes a staged change once the patient types one.
      ...Object.fromEntries(
        guarded.map((contact) => [proposalFieldName(contact.itemId), ""]),
      ),
      hausarzt: currentHausarzt ?? "",
    }),
    [currentHausarzt, guarded, ordinary],
  );

  const categories = useMemo(
    () => buildCategoryViews(contacts, currentHausarzt, enabledFields),
    [contacts, currentHausarzt, enabledFields],
  );

  const [revealed, setRevealed] = useState<ReadonlySet<string>>(new Set());
  // One auto-hide timer per revealed contact: a value shown on a shared screen closes again on its
  // own (KC-56).
  const revealTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const [deleted, setDeleted] = useState<ReadonlySet<string>>(new Set());
  const [additions, setAdditions] = useState<readonly PendingAddition[]>([]);
  const [editing, setEditing] = useState<ReadonlySet<string>>(new Set());
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [failed, setFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // The exact request that was sent, retained so a retry can resend it verbatim (see D2 / the
  // fingerprint rule in submitted-proposal.ts). Never rebuilt from live form state.
  const [submitted, setSubmitted] = useState<SubmittedProposal | null>(null);
  const [outcomes, setOutcomes] = useState<ReadonlyMap<string, FieldOutcome>>(
    new Map(),
  );
  const [nearExpiry, setNearExpiry] = useState(false);
  const [expired, setExpired] = useState(false);
  // A submit in flight must never be torn down by the deadline: the server's own last-attempt
  // degradation is already turning that request into truthful outcomes, and the patient deserves to
  // see them. The expiry is applied once the request settles instead.
  const submittingRef = useRef(false);
  const expiryDueRef = useRef(false);
  // True once the capability was consumed by a submit, so the unmount backstop does not revoke it.
  const consumedRef = useRef(false);
  // Holds a just-scheduled unmount revoke so a remount can cancel it.
  const pendingRevokeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Best-effort revoke backstop: if this step unmounts without a submit having consumed the
  // capability (navigation, reset, timeout), release it server-side. Idempotent and harmless if the
  // capability was already consumed or revoked.
  //
  // The revoke is deferred one tick and cancelled if the effect re-runs (a remount). React StrictMode
  // mounts → unmounts → remounts in development; without this, the simulated unmount would revoke the
  // capability the user is still reviewing and every submit would then be refused. A genuine unmount
  // has no remount to cancel the timer, so it still revokes.
  useEffect(
    function revokeOnUnmount() {
      if (pendingRevokeRef.current !== null) {
        clearTimeout(pendingRevokeRef.current);
        pendingRevokeRef.current = null;
      }
      return function scheduleRevokeIfUnconsumed() {
        if (consumedRef.current || handle === undefined) return;
        pendingRevokeRef.current = setTimeout(function revokeNow() {
          revokePatientInfoQuietly(handle);
        }, 0);
      };
    },
    [handle],
  );

  // `expiresAt` is an Effect DateTime.Utc; reduce it to epoch millis at this boundary so the
  // expiry helpers stay plain arithmetic.
  const expiresAtMs =
    context.patientInfoReview === undefined
      ? undefined
      : DateTime.toEpochMillis(context.patientInfoReview.expiresAt);

  // Two one-shot timers anchored when the review data arrived. Anchoring (rather than polling the
  // wall clock) means a clock change mid-session cannot move the deadline, and the fallback in
  // `initialRemainingMs` means a skewed kiosk clock cannot expire a fresh review on arrival.
  useEffect(
    function scheduleExpiryTimers() {
      if (expiresAtMs === undefined) return;
      const remaining = initialRemainingMs(expiresAtMs, Date.now());
      const noticeIn = Math.max(0, remaining - NEAR_EXPIRY_MS);
      const noticeTimer = setTimeout(function showNearExpiryNotice() {
        setNearExpiry(true);
      }, noticeIn);
      const expiryTimer = setTimeout(function applyExpiry() {
        if (submittingRef.current) {
          expiryDueRef.current = true;
          return;
        }
        setExpired(true);
      }, remaining);
      return function cancelExpiryTimers() {
        clearTimeout(noticeTimer);
        clearTimeout(expiryTimer);
      };
    },
    [expiresAtMs],
  );

  // Entering the expired state is what clears the flow's copy of the patient's data.
  useEffect(
    function announceExpiry() {
      if (expired) send({ type: "PATIENT_INFO_EXPIRED" });
    },
    [expired, send],
  );

  const skip = () => {
    if (handle !== undefined) revokePatientInfoQuietly(handle);
    consumedRef.current = true; // revoked → the unmount backstop must not revoke again
    send({ type: "PATIENT_INFO_SKIP" });
  };

  const form = useReviewForm(defaultValues, async ({ value }) => {
    if (handle === undefined) {
      send({ type: "PATIENT_INFO_SKIP" });
      return;
    }
    const staged = summarizeStagedChanges({
      contacts,
      values: value,
      deleted,
      additions,
      proposals: guarded.flatMap((contact) => {
        const proposed = value[proposalFieldName(contact.itemId)] ?? "";
        return proposed.trim() === "" ? [] : [{ itemId: contact.itemId, value: proposed }];
      }),
      currentHausarzt,
      hausarztInput: value.hausarzt ?? "",
      hausarztEnabled: enabledFields.hausarzt,
    });
    const proposal = buildSubmittedProposal({
      handle,
      submissionId,
      staged,
      contacts,
    });
    setSubmitting(true);
    submittingRef.current = true;
    try {
      const result = await patientInfoRpcClient.submit(proposal.request);
      consumedRef.current = true;
      // Confirming unchanged data is a completed journey in its own right, not a result to read:
      // there is nothing to report back, so it goes straight to the closing screen.
      if (staged.totalCount === 0) {
        send({ type: "PATIENT_INFO_CONFIRMED" });
        return;
      }
      setSubmitted(proposal);
      setOutcomes(mergeOutcomes(new Map(), result.results));
      send({ type: "PATIENT_INFO_SUBMITTED", results: result.results });
    } catch {
      setFailed(true);
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
      if (expiryDueRef.current) setExpired(true);
    }
  });

  /**
   * Resend the identical proposal under the same submission id.
   *
   * The server replays every terminal outcome from its ledger and re-attempts only the
   * `pending-retry` ones, so this is field-scoped in effect while staying inside the
   * one-proposal-per-capability rule. Sending a subset would change the fingerprint and be refused.
   */
  const retry = async () => {
    if (submitted === null) return;
    setSubmitting(true);
    submittingRef.current = true;
    try {
      const result = await patientInfoRpcClient.submit(submitted.request);
      setOutcomes((current) => mergeOutcomes(current, result.results));
    } catch {
      setFailed(true);
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
      if (expiryDueRef.current) setExpired(true);
    }
  };

  const toggleReveal = (contact: ReviewContact) => {
    const timers = revealTimersRef.current;
    const pending = timers.get(contact.itemId);
    if (pending !== undefined) {
      clearTimeout(pending);
      timers.delete(contact.itemId);
    }
    if (!revealed.has(contact.itemId)) {
      timers.set(
        contact.itemId,
        setTimeout(function hideRevealedValue() {
          timers.delete(contact.itemId);
          setRevealed((current) => {
            const next = new Set(current);
            next.delete(contact.itemId);
            return next;
          });
        }, REVEAL_AUTO_HIDE_MS),
      );
    }
    setRevealed((current) => toggle(current, contact.itemId));
  };

  useEffect(function clearRevealTimersOnUnmount() {
    const timers = revealTimersRef.current;
    return function clearRevealTimers() {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  // Read the form store at the top level rather than through nested <form.Subscribe> render props.
  // Nesting them re-created the actions subtree on every keystroke and on submit, which detached the
  // primary button mid-click — the tap was lost and the review sat there until the kiosk reset.
  const values = useStore(form.store, (state) => state.values);
  const canSubmit = useStore(form.store, (state) => state.canSubmit);
  const proposals: readonly PendingProposal[] = guarded.flatMap((contact) => {
    const value = values[proposalFieldName(contact.itemId)] ?? "";
    return value.trim() === "" ? [] : [{ itemId: contact.itemId, value }];
  });
  const staged = summarizeStagedChanges({
    contacts,
    values,
    deleted,
    additions,
    proposals,
    currentHausarzt,
    hausarztInput: values.hausarzt ?? "",
    hausarztEnabled: enabledFields.hausarzt,
  });
  const balance = balanceParts(staged);
  const continueToDone = useCallback(
    () => send({ type: "PATIENT_INFO_CONTINUE" }),
    [send],
  );

  if (expired) {
    return (
      <ExpiredScreen onContinue={continueToDone} />
    );
  }

  if (submitted !== null) {
    return (
      <ResultPhase
        proposal={submitted}
        outcomes={outcomes}
        retrying={submitting}
        onRetry={() => void retry()}
        onContinue={continueToDone}
      />
    );
  }

  const hasStaged = staged.totalCount > 0;
  const primaryLabelKey = reviewPrimaryLabelKey({
    stagedCount: staged.totalCount,
    hasInvalidInput: staged.hasInvalidInput,
  });

  return (
    <KioskStep
      width="default"
      title={t("self-service.check-in.patient-info.review.title-v2")}
      lead={t("self-service.check-in.patient-info.review.intro-v2")}
      primaryAction={{
        label: t(primaryLabelKey),
        icon: hasStaged || staged.hasInvalidInput ? Save : Check,
        onClick: () => void form.handleSubmit(),
        disabled: !canSubmit || staged.hasInvalidInput,
        busy: submitting,
        testId: "patient-info-review-save-button",
      }}
      secondaryAction={{
        label: t("self-service.check-in.patient-info.review.skip"),
        icon: SkipForward,
        onClick: skip,
        disabled: submitting,
        testId: "patient-info-review-skip-button",
      }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit();
        }}
        className="flex min-w-0 flex-col gap-4"
        data-testid="patient-info-review"
      >
        <ul className="m-0 flex list-none flex-col gap-4 p-0">
          {categories.map((view) => {
            const key = view.kind === "hausarzt" ? "hausarzt" : view.category;
            const isEditing = editing.has(key);
            const stagedHere = staged.perCategory[key];
            const chip =
              stagedHere > 0 ? (
                <HandlingChip variant="staged" stagedCount={stagedHere} />
              ) : key === "hausarzt" ? (
                <HandlingChip variant="reception" />
              ) : undefined;
            const isEmpty =
              view.kind === "hausarzt"
                ? view.value === null || view.value.trim() === ""
                : view.primary === undefined;

            return (
              <CategoryCard
                key={key}
                category={key}
                tone={isEditing ? "editing" : "resting"}
                chip={chip}
                actions={
                  <CategoryActions
                    editing={isEditing}
                    empty={isEmpty}
                    revealable={
                      view.kind === "contacts" && view.primary !== undefined
                    }
                    revealed={
                      view.kind === "contacts" &&
                      view.primary !== undefined &&
                      revealed.has(view.primary.itemId)
                    }
                    onToggleReveal={() => {
                      if (
                        view.kind === "contacts" &&
                        view.primary !== undefined
                      )
                        toggleReveal(view.primary);
                    }}
                    onToggleEdit={() =>
                      setEditing((current) => toggle(current, key))
                    }
                  />
                }
              >
                {view.kind === "hausarzt" ? (
                  isEditing ? (
                    <HausarztEditor form={form} current={view.value} />
                  ) : (
                    <HausarztCategoryBody value={view.value} />
                  )
                ) : isEditing ? (
                  <ContactCategoryEditor
                    category={view.category}
                    contacts={[
                      ...(view.primary === undefined ? [] : [view.primary]),
                      ...view.additional,
                    ]}
                    form={form}
                    deleted={deleted}
                    additions={additions.filter(
                      (addition) =>
                        eligibleContactKind(addition.type) === view.category,
                    )}
                    onToggleDelete={(itemId) =>
                      setDeleted((current) => toggle(current, itemId))
                    }
                    onDiscardProposal={(itemId) =>
                      form.setFieldValue(proposalFieldName(itemId), "")
                    }
                    onAdd={(type, value) =>
                      setAdditions((list) => [
                        ...list,
                        {
                          clientItemId: `add-${globalThis.crypto.randomUUID()}`,
                          type,
                          value,
                        },
                      ])
                    }
                    onDiscardAddition={(clientItemId) =>
                      setAdditions((list) =>
                        list.filter(
                          (addition) => addition.clientItemId !== clientItemId,
                        ),
                      )
                    }
                  />
                ) : (
                  <ContactCategoryBody
                    view={view}
                    revealed={revealed}
                    expanded={expanded.has(key)}
                    onToggleExpand={() =>
                      setExpanded((current) => toggle(current, key))
                    }
                    onToggleReveal={toggleReveal}
                  />
                )}
              </CategoryCard>
            );
          })}

          {categories.length === 0 && (
            <li
              className="text-lg text-foreground"
              data-testid="patient-info-review-empty"
            >
              {t("self-service.check-in.patient-info.review.no-contacts")}
            </li>
          )}
        </ul>

        {revealed.size > 0 ? (
          <p className="m-0 text-base text-foreground">
            {t("self-service.check-in.patient-info.review.auto-hide")}
          </p>
        ) : null}

        <ReviewBalanceLine
          balance={
            balance.length === 0
              ? undefined
              : balance
                  .map((part) =>
                    t(
                      part.key === "total"
                        ? "self-service.check-in.patient-info.review.balance-total"
                        : `self-service.check-in.patient-info.review.balance-${part.key}-v2`,
                      { count: part.count },
                    ),
                  )
                  .join(" · ")
          }
        />

        {failed && (
          <KioskStatePanel
            tone="error"
            title={t("self-service.check-in.patient-info.review.save-failed")}
            testId="patient-info-review-failed"
          />
        )}

        {nearExpiry && <NearExpiryNotice />}
      </form>
    </KioskStep>
  );
}

/** The reveal and edit affordances on a category row: labelled 56px buttons. */
function CategoryActions({
  editing,
  empty,
  revealable,
  revealed,
  onToggleReveal,
  onToggleEdit,
}: {
  editing: boolean;
  /** Nothing on file: the action reads "Hinzufügen" rather than "Ändern" (KC-59). */
  empty: boolean;
  revealable: boolean;
  revealed: boolean;
  onToggleReveal: () => void;
  onToggleEdit: () => void;
}) {
  const { t } = useTranslation();
  const EditIcon = editing ? X : empty ? Plus : Pencil;
  return (
    <>
      {revealable && !editing && (
        <RevealButton revealed={revealed} onToggle={onToggleReveal} />
      )}
      <Button
        type="button"
        variant="outline"
        size="2xl"
        onClick={onToggleEdit}
        aria-expanded={editing}
        data-testid="patient-info-category-edit-button"
      >
        <EditIcon aria-hidden className="size-6" />
        {t(
          editing
            ? "self-service.check-in.patient-info.review.close"
            : empty
              ? "self-service.check-in.patient-info.review.add"
              : "self-service.check-in.patient-info.review.change",
        )}
      </Button>
    </>
  );
}

/** The Hausarzt free-text editor: every change becomes a reception task, never a direct write. */
function HausarztEditor({
  form,
  current,
}: {
  form: ReturnType<typeof useReviewForm>;
  current: string | null;
}) {
  const { t } = useTranslation();
  return (
    <form.Field
      name="hausarzt"
      validators={{ onChange: Schema.toStandardSchemaV1(HausarztInput) }}
    >
      {(field) => (
        <div className="mt-1 flex min-w-0 flex-col gap-2">
          <KioskTextField
            id="patient-info-hausarzt"
            testId="patient-info-hausarzt-input"
            errorTestId="patient-info-hausarzt-error"
            label={t("self-service.check-in.patient-info.review.hausarzt-placeholder")}
            icon={Stethoscope}
            maxLength={120}
            value={field.state.value}
            onBlur={field.handleBlur}
            onChange={field.handleChange}
            description={[
              current !== null && current.trim() !== ""
                ? t("self-service.check-in.patient-info.review.hausarzt-previous", {
                    value: current,
                  })
                : null,
              t("self-service.check-in.patient-info.review.hausarzt-counter", {
                count: field.state.value.length,
              }),
            ]
              .filter((part) => part !== null)
              .join(" · ")}
            error={
              field.state.meta.errors.length > 0
                ? t("self-service.check-in.patient-info.review.hausarzt-invalid")
                : undefined
            }
          />
        </div>
      )}
    </form.Field>
  );
}

/** Add or remove an id from a readonly set, returning a new set. */
function toggle(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}


/**
 * The result phase: the same rows the patient just reviewed, each carrying what became of it.
 *
 * Only submitted fields appear. `complete: false` is never dressed up as success — while anything is
 * unfinished the banner stays red and names the problem, and "Weiter" remains available because the
 * check-in itself was completed long before this optional review began.
 */
function ResultPhase({
  proposal,
  outcomes,
  retrying,
  onRetry,
  onContinue,
}: {
  proposal: SubmittedProposal;
  outcomes: ReadonlyMap<string, FieldOutcome>;
  retrying: boolean;
  onRetry: () => void;
  onContinue: () => void;
}) {
  const { t } = useTranslation();
  const unfinished = hasUnfinishedOutcome(outcomes);

  return (
    <KioskStep
      width="default"
      title={t("self-service.check-in.patient-info.review.title-v2")}
      primaryAction={{
        label: t("self-service.check-in.patient-info.result.continue"),
        icon: ArrowRight,
        onClick: onContinue,
        testId: "patient-info-result-continue-button",
      }}
    >
      <div className="flex min-w-0 flex-col gap-4" data-testid="patient-info-result">
        <output
          className={`m-0 block rounded-2xl border px-5 py-4 text-lg font-medium text-foreground ${
            unfinished
              ? "border-destructive-subtle bg-destructive-soft"
              : "border-success-subtle bg-success-soft"
          }`}
          aria-live="polite"
          data-testid="patient-info-result-banner"
          data-unfinished={unfinished}
        >
          {t(
            unfinished
              ? "self-service.check-in.patient-info.result.banner-unfinished"
              : "self-service.check-in.patient-info.result.banner-done-v2",
          )}
        </output>

        <ul className="m-0 flex list-none flex-col gap-4 p-0">
          {proposal.entries.map((entry) => {
            const outcome = outcomes.get(entry.key);
            return outcome === undefined ? null : (
              <OutcomeRow
                key={entry.key}
                entry={entry}
                outcome={outcome}
                retrying={retrying}
                onRetry={onRetry}
              />
            );
          })}
        </ul>

        <p className="m-0 text-lg text-foreground">
          {t("self-service.check-in.patient-info.result.footer-v2")}
        </p>
      </div>
    </KioskStep>
  );
}
